import { format } from 'date-fns';
// @mui
import { Avatar, Typography, Stack } from '@mui/material';
// auth
import { useUserContext } from '../../../../auth/useUserContext';
// @types
import { IChatConversation, IChatMessage } from '../../../../@types/chat';
// components
import Image from '../../../../components/image';

// ----------------------------------------------------------------------

type Props = {
  message: IChatMessage;
  conversation: IChatConversation;
  onOpenLightbox: (value: string) => void;
  isGrouped?: boolean;
};

export default function ChatMessageItem({ message, conversation, onOpenLightbox, isGrouped = false }: Props) {
  const { data: userData } = useUserContext();
  
  const sender = conversation.participants.find(
    (participant) => participant.id === message.senderId
  );

  const senderDetails =
    message.senderId === userData?.id
      ? {
          type: 'me',
        }
      : {
          avatar: sender?.avatar,
          name: sender?.name,
        };

  const currentUser = senderDetails.type === 'me';

  const isImage = message.contentType === 'image';

  const firstName = senderDetails.name && senderDetails.name.split(' ')[0];

  return (
    <Stack direction="row" justifyContent={currentUser ? 'flex-end' : 'unset'} sx={{ mb: isGrouped ? 0.5 : 3 }}>
      {!currentUser && (
        <Avatar
          alt={senderDetails.name}
          src={senderDetails.avatar}
          sx={{ 
            width: 32, 
            height: 32, 
            mr: 2,
            ...(isGrouped && { opacity: 0, visibility: 'hidden' }) // Hide avatar for grouped messages but maintain spacing
          }}
        />
      )}

      <Stack spacing={1} alignItems={currentUser ? "flex-end" : "flex-start"}>
        {!isGrouped && (
          <Typography
            noWrap
            variant="caption"
            sx={{
              color: 'text.disabled',
              ...(!currentUser && {
                mr: 'auto',
              }),
            }}
          >
            {!currentUser && `${firstName},`} &nbsp;
            {format(new Date(message.createdAt), 'yyyy-MM-dd HH:mm')}
          </Typography>
        )}

        <Stack
          sx={{
            p: 1.5,
            minWidth: 48,
            maxWidth: 320,
            borderRadius: 1,
            overflow: 'hidden',
            typography: 'body2',
            bgcolor: 'background.neutral',
            ...(currentUser && {
              color: 'grey.800',
              bgcolor: 'primary.lighter',
            }),
            ...(isImage && {
              p: 0,
            }),
          }}
        >
          {isImage ? (
            <Image
              alt="attachment"
              src={message.body}
              onClick={() => onOpenLightbox(message.body)}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.9,
                },
              }}
            />
          ) : (
            message.body
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
